export type AnalyticsFetchContext = {
  studentId?: string;
  lessonId?: number;
  unitId?: number;
  courseId?: number;
};

type ApiErrorEventData = {
  endpoint: string;
  status_code: number;
  error_message: string;
};

type ApiErrorTracker = (
  eventType: "api_error",
  eventData: ApiErrorEventData,
  context?: AnalyticsFetchContext
) => void;

let apiErrorTracker: ApiErrorTracker | null = null;
let hasWarnedMissingTracker = false;

export function setApiErrorTracker(tracker: ApiErrorTracker | null): void {
  apiErrorTracker = tracker;
  if (tracker) {
    hasWarnedMissingTracker = false;
  }
}

const trackApiError = (
  eventData: ApiErrorEventData,
  context?: AnalyticsFetchContext
) => {
  if (!apiErrorTracker) {
    if (import.meta.env.DEV && !hasWarnedMissingTracker) {
      console.warn("[analytics] api_error tracker is not registered yet.");
      hasWarnedMissingTracker = true;
    }
    return;
  }

  apiErrorTracker("api_error", eventData, context);
};

const getEndpointFromInput = (input: RequestInfo | URL): string => {
  if (typeof input === "string") {
    return extractEndpoint(input);
  }
  if (input instanceof URL) {
    return extractEndpoint(input.toString());
  }
  return extractEndpoint(input.url);
};

const extractEndpoint = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
};

const getResponseErrorMessage = async (response: Response): Promise<string> => {
  try {
    const text = await response.clone().text();
    const trimmed = text.trim();
    if (trimmed) {
      return trimmed.slice(0, 500);
    }
  } catch {
    // Ignore clone/read errors and fall back to status text.
  }
  return response.statusText || `HTTP ${response.status}`;
};

export async function fetchWithApiError(
  input: RequestInfo | URL,
  init?: RequestInit,
  context?: AnalyticsFetchContext
): Promise<Response> {
  const endpoint = getEndpointFromInput(input);
  try {
    const response = await fetch(input, init);
    if (!response.ok) {
      const errorMessage = await getResponseErrorMessage(response);
      trackApiError(
        {
          endpoint,
          status_code: response.status,
          error_message: errorMessage,
        },
        context
      );
    }
    return response;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Network error";
    trackApiError(
      {
        endpoint,
        status_code: 0,
        error_message: message,
      },
      context
    );
    throw error;
  }
}
